import { View } from "@/components/view/view";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import DemoSignupLayout from "./demoSignupLayout";
import { Text } from "@/components/text/text";
import ShakeEffectView from "@/components/animatableView/shakeEffectView";
import { FormikProvider, useFormik } from "formik";
import ValidationIndicator from "@/components/validationIndicator/validationIndicator";
import { createValidator, DataCy, DoorLoopLeadDto, DoorLoopLeadSources, SegmentEventTypes } from "@/dto";
import { useHistory, useLocation, useParams } from "react-router-dom";
import noop from "lodash/noop";
import set from "lodash/set";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import intersection from "lodash/intersection";
import keys from "lodash/keys";
import qs from "query-string";
import { analyticsService } from "services/analyticsService";
import { useInjectClickCaseScript } from "./clickCeaseScript";
import DLButton from "@/components/button/buttonUI";
import { DLButtonColorsEnum, DLButtonSizesEnum } from "@/components/button/enums";
import { useResponsiveHelper } from "@/contexts/responsiveContext";
import ViewSlider from "react-view-slider";
import { useEffectAsync } from "hooks/useEffectAsync";
import { Link } from "@/components/link/link";
import { MobileScreenContainerDefaultPadding } from "contexts/utils";
import { useAnalyticsService } from "hooks/useAnalyticsService";
import { demoFormUtils, getDLMCCookie, getSalesforceUuid } from "./demo.utils";
import { computeLeadScoreBreakdown } from "./leadScoreCalculator";
import { useDemoFormVersion } from "@/hooks/useDemoFormVersion";
import { DEMO_ADDITIONAL_INFO_BASE_ROUTE, DEMO_SCHEDULE_ROUTE } from "@/components/router";
import { ContactDetailsStep } from "@/pages/demo/steps/contactDetailsStep";
import { UserDetailsStep } from "@/pages/demo/steps/userDetailsStep";
import { ScheduleDemoStep } from "./steps/scheduleDemoStep";
import { useFormStorage } from "@/pages/demo/hooks/useFormStorage";
import { WizardStepper } from "components/wizard";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { FORM_INNER_WIDTH, STEPPER_LABELS } from "@/pages/demo/demoForm";
import { PARAM_TO_VIEW_MAP, StepId, VIEW_TO_PARAM_MAP, ViewNames } from "@/pages/demo/constants/views";
import { HUBSPOT_CONTACT_DETAILS_FORM_ID, HUBSPOT_USER_DETAILS_FORM_ID } from "./constants/hubspot";
import { useStopwatch } from "@/utils/stopwatch";

interface IPDetails {
  country: string;
  isUsingVPN: boolean;
  ip: string;
}

// Define a type that extends DoorLoopLeadDto with UTM parameters
interface ExtendedDoorLoopLeadDto extends DoorLoopLeadDto {
  utm_source?: string;
  utm_term?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_campaign?: string;
}

const initialQueryParamsMap: Record<string, keyof ExtendedDoorLoopLeadDto> = {
  email: "email",
  full_name: "name",
  phone: "phone",
  expected_number_of_units__c: "numberOfUnits",
  primary_portfolio__type: "mainPropertyType",
  customer_reported_timeline__c: "whenPlanToUseSoftware",
  user_selected_occupation: "selectedJobTitle",
  utm_source: "utm_source",
  utm_term: "utm_term",
  utm_medium: "utm_medium",
  utm_content: "utm_content",
  utm_campaign: "utm_campaign"
};

const PRIVACY_POLICY_URL = "https://www.doorloop.com/legal/privacy";
const TERMS_OF_SERVICE_URL = "https://www.doorloop.com/legal/terms";
const PROXYCHECK_TIMEOUT_MS = 4000;

const getIPDetails = async (): Promise<IPDetails | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROXYCHECK_TIMEOUT_MS);
  demoFormUtils.track("geo_detection_started", { provider: "proxycheck.io" });
  try {
    const proxyResponse = await fetch(`https://proxycheck.io/v2/?key=public-4g8i9m-1696rc-e43638&risk=1&vpn=1&asn=1`, {
      method: "GET",
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    if (!proxyResponse.ok) {
      demoFormUtils.track("geo_detection_failed", {
        provider: "proxycheck.io",
        reason: "http_error",
        status: proxyResponse.status
      });
      return null;
    }
    const data = await proxyResponse.json();
    if (!data || data.status !== "ok") {
      demoFormUtils.track("geo_detection_failed", { provider: "proxycheck.io", reason: "non_ok_status" });
      return null;
    }
    // proxycheck.io v2 keys the response by the resolved IP, not under a
    // `data.ip` property. Find the only key that isn't a metadata field.
    const RESERVED_KEYS = new Set(["status", "node", "message"]);
    const ipKey = Object.keys(data).find((k) => !RESERVED_KEYS.has(k));
    if (!ipKey || typeof data[ipKey] !== "object") {
      demoFormUtils.track("geo_detection_failed", { provider: "proxycheck.io", reason: "no_ip_key" });
      return null;
    }
    const ipDetails = data[ipKey];
    demoFormUtils.track("geo_detection_succeeded", {
      provider: "proxycheck.io",
      country: ipDetails.country ?? "",
      proxy: ipDetails.proxy ?? "no"
    });
    return {
      country: ipDetails.country ?? "",
      isUsingVPN: ipDetails.proxy === "yes",
      ip: ipKey
    };
  } catch (error) {
    clearTimeout(timeoutId);
    demoFormUtils.track("geo_detection_failed", {
      provider: "proxycheck.io",
      reason: error instanceof Error && error.name === "AbortError" ? "timeout" : "network_error",
      error_message: error instanceof Error ? error.message : String(error)
    });
    return null;
  }
};

const getCurrentActiveView = (stepId?: StepId, pathname?: string): ViewNames => {
  if (pathname && pathname.startsWith(DEMO_SCHEDULE_ROUTE)) {
    return ViewNames.ScheduleDemo;
  }
  if (!stepId) {
    return ViewNames.ContactDetails;
  }
  return PARAM_TO_VIEW_MAP[stepId];
};

export function DemoAdditionalInfo() {
  const { isMobile } = useResponsiveHelper();
  const [showErrorShakeEffect, setShowErrorShakeEffect] = useState(false);
  const [arrivedWithInvalidEmail, setArrivedWithInvalidEmail] = useState(false);
  const [loadingInProgress, setLoadingInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { demoFormVersionValues } = useDemoFormVersion();

  const location = useLocation();
  const { stepId } = useParams<{ stepId?: StepId }>();
  const initialView = getCurrentActiveView(stepId, location.pathname);
  const [currentActiveView, setCurrentActiveView] = useState<ViewNames>(initialView);
  const fireIntercomEventHandler = useRef<() => void>(noop);
  const IPDetailsRef = useRef<IPDetails | null>(null);
  // Tracks whether the component is still mounted, so post-await state
  // writes inside useEffectAsync don't fire on an unmounted component.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Eagerly capture ?salesforce_uuid= from the URL at mount, BEFORE
  // navigateToStep strips the query string via history.push({ pathname }).
  // getSalesforceUuid() validates and caches on window.__salesforceUuid;
  // subsequent calls (in appendCalendlyParams) return the cached value
  // even after the URL no longer carries the param.
  useEffect(() => {
    getSalesforceUuid();
  }, []);
  const { startSessionRecordingWithoutFeatureFlag } = useAnalyticsService();

  const stopwatch = useStopwatch();

  const history = useHistory();
  const { formData, saveStepData } = useFormStorage();

  const { executeRecaptcha } = useRecaptcha({
    action: "demo_form_submit",
    onSuccess: ({ response, email, timeToSubmit }) => {
      demoFormUtils.track("recaptcha_v3_scored", {
        score: response.recaptcha_v3_score,
        email,
        time_to_submit_ms: Math.round(timeToSubmit)
      });
    }
  });

  const hadEmailInQueryString = useMemo(() => {
    const { email } = qs.parse(window.location.search);
    return Boolean(email);
  }, []);

  const initFormValues = useMemo(() => {
    const signupDto = new DoorLoopLeadDto();
    const queryString = qs.parse(window.location.search);
    signupDto.email ||= queryString.email as string;

    if (formData.contactDetailsStep) {
      Object.assign(signupDto, { ...formData.contactDetailsStep, ...formData.userDetailsStep });
    }

    Object.entries(initialQueryParamsMap).forEach(([queryKey, fieldName]) => {
      if (queryString[queryKey]) {
        const value = fieldName === "numberOfUnits" ? Number(queryString[queryKey]) : queryString[queryKey];
        Object.assign(signupDto, { [fieldName]: value });
      }
    });

    return signupDto;
  }, [formData]);

  const formik = useFormik<DoorLoopLeadDto>({
    initialValues: initFormValues,
    validate: createValidator(DoorLoopLeadDto),
    onSubmit: noop,
    validateOnBlur: true,
    validateOnChange: true,
    // Run validation on mount so the validator is warm by the time the user
    // clicks Continue for the first time. Without this, the first
    // setFieldTouched + validateForm chain on submit triggers a one-shot
    // initialisation that the user perceives as a flicker.
    validateOnMount: true
  });

  useEffect(() => {
    fireIntercomEventHandler.current = () => {
      demoFormUtils.track("user_clicked_intercom_bubble", { step: currentActiveView });
    };
  }, [currentActiveView]);

  useEffect(() => {
    analyticsService.page();
  }, []);

  useEffect(() => {
    const activeView = getCurrentActiveView(stepId, location.pathname);
    if (activeView !== currentActiveView) {
      setCurrentActiveView(activeView);
    }
  }, [stepId, location.pathname]);

  useEffectAsync(async () => {
    const formValues = formik.values;
    const email = formValues?.email;

    if (email) {
      analyticsService.identifyLead(
        email,
        pick(formValues, ["name", "phone", "numberOfUnits"]) as Record<string, string>
      );
      demoFormUtils.track(
        "lead_created",
        {
          lead_created_location: window.location.pathname,
          lead_created_pms_post: false
        },
        { email }
      );
    }

    const leadSource =
      new URLSearchParams(window.location.search).get("leadsource") === "Scheduled Call"
        ? DoorLoopLeadSources.SCHEDULE_CALL
        : DoorLoopLeadSources.SCHEDULE_DEMO;
    formik.setFieldValue("leadSource", leadSource);

    let ipDetails: IPDetails | null = null;
    try {
      const res = await getIPDetails();
      if (!isMountedRef.current) return;
      if (res) {
        ipDetails = res;
        IPDetailsRef.current = res;
        const { ip, country, isUsingVPN } = res;

        formik.setFieldValue("ipAddress", ip);
        formik.setFieldValue("country", country);
        formik.setFieldValue("isUsingVPN", isUsingVPN);
      }
    } catch (error) {
      // getIPDetails already tracks geo_detection_failed internally; nothing
      // else to surface to the user.
    }

    if (!isMountedRef.current) return;

    try {
      const emailValidationResult = await demoFormUtils.hubspot.createEmailOnlyLead({
        ...formValues,
        leadSource,
        ipAddress: ipDetails?.ip,
        country: ipDetails?.country,
        isUsingVPN: ipDetails?.isUsingVPN,
        posthog_distinct_id: window.posthog?.get_distinct_id?.()
      });
      if (!isMountedRef.current) return;

      // Only treat the email field as invalid when the validation service
      // explicitly classified the address. Network/transient failures must
      // NOT block the user with a misleading "invalid email" error.
      if (
        emailValidationResult &&
        emailValidationResult.ok === false &&
        emailValidationResult.reason === "invalid_email"
      ) {
        formik.setFieldError("email", "Please enter a valid email");
        formik.setFieldTouched("email", true, false);
        setArrivedWithInvalidEmail(true);
      }
    } catch (error) {
      // Genuinely unexpected — let the user proceed; tracker fired downstream.
    }

    if (isMountedRef.current) {
      startSessionRecordingWithoutFeatureFlag();
    }
  }, []);

  useInjectClickCaseScript();

  const runShakeEffect = () => {
    setShowErrorShakeEffect(true);
    setTimeout(() => {
      setShowErrorShakeEffect(false);
    }, 300);
  };

  const isFirstStepValid = async () => {
    formik.setFieldTouched("email");
    formik.setFieldTouched("name");
    formik.setFieldTouched("phone");
    formik.setFieldTouched("numberOfUnits");

    const [errors, { isValid }] = await Promise.all([
      formik.validateForm(),
      demoFormUtils.validateEmail(formik.values.email)
    ]);

    if (!isValid) {
      formik.setFieldError("email", "Please enter a valid email");
      formik.setFieldTouched("email", true, false);
    }

    const valid = isValid && isEmpty(intersection(keys(errors), ["email", "name", "phone", "numberOfUnits"]));

    if (valid) {
      saveStepData("contactDetailsStep", pick(formik.values, ["email", "name", "phone", "numberOfUnits"]));
    }

    return valid;
  };

  const isSecondStepValid = async () => {
    formik.setFieldTouched("selectedJobTitle");
    formik.setFieldTouched("jobTitle");
    formik.setFieldTouched("mainPropertyType");
    formik.setFieldTouched("whenPlanToUseSoftware");
    const errors = await formik.validateForm();
    const valid = isEmpty(
      intersection(keys(errors), ["selectedJobTitle", "jobTitle", "mainPropertyType", "whenPlanToUseSoftware"])
    );

    if (valid) {
      saveStepData(
        "userDetailsStep",
        pick(formik.values, [
          "selectedJobTitle",
          "jobTitle",
          "doYouManagePropertiesInTheUSOrCanada",
          "mainPropertyType",
          "whenPlanToUseSoftware"
        ])
      );
    }

    return valid;
  };

  const didPressScheduleDemo = async (values: DoorLoopLeadDto) => {
    setLoadingInProgress(true);

    try {
      if (currentActiveView === ViewNames.ContactDetails) {
        const isValid = await isFirstStepValid();

        if (!isValid) {
          runShakeEffect();
          return;
        }

        let firstStepValues = pick(values, ["name", "phone", "numberOfUnits"]) as Record<string, string>;

        demoFormUtils.track("lead_filled_out_first_demo_form_section", firstStepValues, firstStepValues);
        analyticsService.identifyLead(values.email || "", firstStepValues);

        demoFormUtils.hubspot.reportToHubSpot(values, HUBSPOT_CONTACT_DETAILS_FORM_ID);
        navigateToStep(ViewNames.UserDetails);
        demoFormUtils.track("second_demo_form_step_loaded");
        return;
      }

      if (currentActiveView === ViewNames.UserDetails) {
        const isValid = await isSecondStepValid();

        if (!isValid) {
          runShakeEffect();
          return;
        }

        // GTM-110: reCAPTCHA v3 disabled in the demo form flow per RevOps decision.
        // Hook (useRecaptcha) and executeRecaptcha helper are intentionally kept
        // wired in this file so they can be re-enabled with a single revert.
        // Do NOT invoke executeRecaptcha here.
        void executeRecaptcha;
        void stopwatch;

        // Kick off the HubSpot Pt.2 submission in parallel; we'll await it
        // before any window.location.href redirect so the POST isn't canceled
        // by browser-level navigation. SPA navigations (Calendly embed) don't
        // cancel in-flight fetches, so awaiting is only required on the
        // arbitrary_url branch — but starting it here lets it run alongside
        // analytics + getCalendlyURL.
        const hubspotPt2Promise = demoFormUtils.hubspot.reportToHubSpot(values, HUBSPOT_USER_DETAILS_FORM_ID);

        // Compute lead score ONCE here with full breakdown — fires the
        // pass/fail event exactly once per submission. Previously these
        // events fired 2-3× per submit because calculateLeadScore was
        // invoked from multiple call sites.
        const leadScoreBreakdown = computeLeadScoreBreakdown(values, getDLMCCookie());
        const leadScoreEventName = leadScoreBreakdown.passed ? "lead_passed_lead_scoring" : "lead_failed_lead_scoring";
        demoFormUtils.track(
          leadScoreEventName,
          {
            leadScore: leadScoreBreakdown.total,
            unitsScore: leadScoreBreakdown.components.units,
            sourceScore: leadScoreBreakdown.components.source,
            timelineScore: leadScoreBreakdown.components.timeline,
            propertyTypeScore: leadScoreBreakdown.components.propertyType,
            roleScore: leadScoreBreakdown.components.role,
            mainPropertyType: leadScoreBreakdown.inputs.mainPropertyType ?? "",
            selectedJobTitle: leadScoreBreakdown.inputs.selectedJobTitle ?? "",
            whenPlanToUseSoftware: leadScoreBreakdown.inputs.whenPlanToUseSoftware ?? "",
            utmSource: leadScoreBreakdown.inputs.utmSource ?? "",
            numberOfUnits: leadScoreBreakdown.inputs.numberOfUnits
          },
          { leadScore: leadScoreBreakdown.total }
        );

        const cleanSegmentPayload = pick(values, [
          "primary_portfolio__type",
          "customer_reported_timeline__c",
          "numberOfUnits",
          "selectedJobTitle"
        ]) as Record<string, string>;

        // Fires lead_submitted_personal_info_form once across PostHog,
        // gtag, fbq (Meta Pixel), and uetq (Bing UET) for ad-conversion
        // tracking. Replaces the previous duplicate fire that emitted
        // the same PostHog event twice with different payloads.
        analyticsService.track(SegmentEventTypes.LEAD_SUBMITTED_PERSONAL_INFO_FORM, {
          category: "Leads",
          label: "Demo",
          custom_lead_score: leadScoreBreakdown.total,
          ...cleanSegmentPayload
        });

        setErrorMessage(undefined);

        const { result_url, result_type } = await demoFormUtils.getCalendlyURL(values);

        if (result_type === "arbitrary_url") {
          demoFormUtils.track("routing_redirect", { url: result_url, leadScore: leadScoreBreakdown.total });
          // Await the HubSpot Pt.2 POST (with a 5s safety timeout) before
          // navigating away. Without this, the browser cancels the in-flight
          // request, leaving Pt.2 fields (user_selected_occupation,
          // primary_portfolio__type, customer_reported_timeline__c, lead score)
          // empty on the HubSpot contact for redirect-disqualified leads —
          // breaking sales handoff and downstream automation.
          await Promise.race([hubspotPt2Promise, new Promise<void>((resolve) => setTimeout(resolve, 5000))]);
          window.location.href = result_url;
        } else {
          demoFormUtils.track("routing_embed_calendly", {
            url: result_url,
            leadScore: leadScoreBreakdown.total,
            numberOfUnits: leadScoreBreakdown.inputs.numberOfUnits
          });
          const params = new URLSearchParams({
            result_url
          });
          navigateToStep(ViewNames.ScheduleDemo, params);
        }
      }
    } finally {
      setLoadingInProgress(false);
    }
  };

  const navigateToStep = (viewId: ViewNames, searchParams?: URLSearchParams) => {
    setCurrentActiveView(viewId);

    if (viewId === ViewNames.ScheduleDemo) {
      const stringifyParams = searchParams?.toString();

      history.push({
        pathname: DEMO_SCHEDULE_ROUTE,
        search: searchParams ? stringifyParams : undefined
      });
      return;
    }

    history.push({
      pathname: `${DEMO_ADDITIONAL_INFO_BASE_ROUTE}/${VIEW_TO_PARAM_MAP[viewId]}`
    });
  };

  const didPressPrivacyPolicy = async () => {
    demoFormUtils.track("user_clicked_privacy_policy", { step: currentActiveView });
  };

  // Form views have an internal paddingTop so their fields render BELOW the
  // absolute-positioned header overlay above the slider. Schedule view
  // doesn't need this padding — the calendar sits at the slider's top so
  // there's only the small wrapper paddingTop between stepper and calendar.
  const formViewPaddingTop = isMobile ? 64 : 80;
  const viewsArray = [
    () => (
      <div style={{ paddingTop: formViewPaddingTop, width: "100%" }}>
        <ContactDetailsStep shouldDisplayEmail={!hadEmailInQueryString || arrivedWithInvalidEmail} />
      </div>
    ),
    () => (
      <div style={{ paddingTop: formViewPaddingTop, width: "100%" }}>
        <UserDetailsStep />
      </div>
    ),
    () => <ScheduleDemoStep />
  ];

  const documentTitle = useMemo(() => {
    if (currentActiveView === ViewNames.ContactDetails || currentActiveView === ViewNames.UserDetails) {
      return "Schedule Demo - Additional Info";
    } else if (currentActiveView === ViewNames.ScheduleDemo) {
      return "Schedule Demo - Schedule";
    }

    return "Schedule Demo";
  }, [currentActiveView]);

  return (
    <DemoSignupLayout
      hideMarketingView={currentActiveView === ViewNames.ScheduleDemo ? false : true}
      documentTitle={documentTitle}
      gap={0}
    >
      <FormikProvider value={formik}>
        {/* Pre-mount hidden MUI primitives so JSS injects all needed
            keyframes/styles at form-load time. Without this, the FIRST time
            the Continue button transitions to its loading/disabled state,
            JSS injection triggers a one-shot reflow that the user perceives
            as a flicker. The hidden DLButton in loading state warms BOTH
            the disabled button background AND the spinner; the bare
            CircularProgress is belt-and-braces. */}
        <div
          aria-hidden
          style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0, pointerEvents: "none" }}
        >
          <CircularProgress />
          <DLButton
            actionText={"Continue"}
            isLoading={true}
            size={DLButtonSizesEnum.LARGE}
            color={DLButtonColorsEnum.SUCCESS}
          />
        </div>
        <View paddingLeft={isMobile ? 0 : 40} paddingRight={isMobile ? 0 : 40}>
          <View
            paddingLeft={isMobile ? 10 : 40}
            paddingRight={isMobile ? 10 : 40}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <WizardStepper activeStep={currentActiveView} wizardSteps={STEPPER_LABELS} setActiveStep={navigateToStep} />
            {/* Header is an absolutely-positioned overlay on BOTH platforms.
                The wrapper reserves a fixed paddingTop for the header's space
                (also the "padding from the top" above the calendar on the
                schedule step). The slider's Y position is constant across
                all 3 steps — calendar never moves vertically, fields never
                drift up. Header animates via opacity + a small translateY.
                Form views push their content down with their own paddingTop
                so fields render below the absolute header. */}
            <div
              style={{
                position: "relative",
                width: "100%",
                paddingTop: isMobile ? 16 : 30
              }}
            >
              <div
                aria-hidden={currentActiveView === ViewNames.ScheduleDemo}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  opacity: currentActiveView !== ViewNames.ScheduleDemo ? 1 : 0,
                  transform: currentActiveView === ViewNames.ScheduleDemo ? "translateY(-16px)" : "translateY(0)",
                  transition:
                    "opacity 300ms cubic-bezier(0.4, 0, 0.2, 1), transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                  pointerEvents: currentActiveView === ViewNames.ScheduleDemo ? "none" : "auto"
                }}
              >
                <Text
                  fontSize={isMobile ? 24 : 30}
                  fontWeight={900}
                  textTransform={"uppercase"}
                  value={"REQUEST A DEMO"}
                  marginTop={isMobile ? 5 : 20}
                />

                <Text
                  fontSize={isMobile ? 16 : 20}
                  value={demoFormVersionValues.firstStep.subheader}
                  marginTop={isMobile ? 5 : 10}
                  align={"center"}
                />
              </div>
              <ViewSlider
                style={{ width: "100%" }}
                renderView={({ index }) => viewsArray[index]?.()}
                numViews={3}
                animateHeight={true}
                transitionDuration={300}
                transitionTimingFunction={"cubic-bezier(0.4, 0, 0.2, 1)"}
                activeView={currentActiveView}
                innerViewWrapper={{ display: "flex", height: "100%", width: "100%" }}
              />
            </div>
            <div
              aria-hidden={currentActiveView === ViewNames.ScheduleDemo}
              style={{
                maxHeight: currentActiveView !== ViewNames.ScheduleDemo ? 120 : 0,
                opacity: currentActiveView !== ViewNames.ScheduleDemo ? 1 : 0,
                overflow: "hidden",
                transition: "max-height 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                width: "100%",
                pointerEvents: currentActiveView !== ViewNames.ScheduleDemo ? "auto" : "none"
              }}
            >
              <ShakeEffectView fullWidth showEffect={showErrorShakeEffect}>
                <View alignItems={"center"} marginTop={20}>
                  <DLButton
                    onClick={async () => await didPressScheduleDemo(formik.values)}
                    actionText={"Continue"}
                    size={DLButtonSizesEnum.LARGE}
                    isLoading={loadingInProgress}
                    style={{ width: "100%" }}
                    color={DLButtonColorsEnum.SUCCESS}
                    dataCy={DataCy.marketing.demoForm.continueButton}
                  />
                </View>
              </ShakeEffectView>
            </div>
            <div
              aria-hidden={currentActiveView !== ViewNames.ContactDetails}
              style={{
                maxHeight: currentActiveView === ViewNames.ContactDetails ? 80 : 0,
                opacity: currentActiveView === ViewNames.ContactDetails ? 1 : 0,
                overflow: "hidden",
                transition: "max-height 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                width: "100%",
                pointerEvents: currentActiveView === ViewNames.ContactDetails ? "auto" : "none"
              }}
            >
              <View
                flexDirection={"column"}
                marginTop={20}
                paddingLeft={MobileScreenContainerDefaultPadding}
                paddingRight={MobileScreenContainerDefaultPadding}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <View flexDirection={"row"} marginTop={8} justifyContent={"center"}>
                  <Text fontSize={12} value={"By continuing, you agree to DoorLoop's"} />
                  <Link
                    hrefUrl={PRIVACY_POLICY_URL}
                    underline={"always"}
                    hoverColor={"blue"}
                    type={"blank"}
                    onClick={async () => await didPressPrivacyPolicy()}
                  >
                    <Text marginLeft={3} fontSize={12} className="lg:!whitespace-nowrap">
                      Privacy Policy
                    </Text>
                  </Link>
                  <Text marginLeft={3} fontSize={12} value={"&"} />
                  <Link hrefUrl={TERMS_OF_SERVICE_URL} underline={"always"} hoverColor={"blue"} type={"blank"}>
                    <Text marginLeft={3} fontSize={12} className="lg:!whitespace-nowrap">
                      Terms
                    </Text>
                  </Link>
                  <Text fontSize={12} className="lg:!whitespace-nowrap">
                    .
                  </Text>
                </View>
              </View>
            </div>
            <ValidationIndicator
              shouldShow={errorMessage !== undefined}
              displayText={errorMessage || ""}
              wrapperWidth={FORM_INNER_WIDTH + "px"}
            />
          </View>
        </View>
      </FormikProvider>
    </DemoSignupLayout>
  );
}
